import { useCallback } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import {
  EnvelopeIcon,
  EyeSlashIcon,
  PencilIcon,
  SparklesIcon,
  CloudIcon,
  TruckIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  MtmTransportationProviderRead,
  useBrokersMtmInitCreateMutation,
  useBrokersMtmTransportationProvidersSetSecretCreateMutation,
  useLazyBrokersMtmTransportationProvidersGetEmailRetrieveQuery,
  useLazyBrokersMtmTransportationProvidersTurnbackReasonsRetrieveQuery,
  useBrokersMtmTransportationProvidersBackfillTripsCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { useCopyText } from 'src/features/billing/invoices/modals/hooks/useCopyText';

import { adminActions } from '../../admin.slice';

export default function BackfillTripsIcon({ className }: { className?: string }) {
  return (
    <div className={`relative ${className || 'w-6 h-6'}`}>
      <TruckIcon className="w-6 h-6 text-gray-700" />
      <ArrowUturnLeftIcon className="w-3 h-3 text-blue-500 absolute -top-1 -right-1 bg-white rounded-full p-0.5" />
    </div>
  );
}

interface ManageCellProps {
  setting: MtmTransportationProviderRead;
}
export function ManageCell({ setting }: ManageCellProps) {
  const dispatch = useAppDispatch();
  const copy = useCopyText();
  const [triggerInitializeMtmTransportationProvider] =
    useBrokersMtmInitCreateMutation();

  const [triggerGetEmail] =
    useLazyBrokersMtmTransportationProvidersGetEmailRetrieveQuery();

  const [triggerSetSecret] =
    useBrokersMtmTransportationProvidersSetSecretCreateMutation();

  const [triggerCheckStatus] =
    useLazyBrokersMtmTransportationProvidersTurnbackReasonsRetrieveQuery();

  const [triggerBackfillTrips] =
    useBrokersMtmTransportationProvidersBackfillTripsCreateMutation();

  // TODO: Uncomment this if we support deleting MTM settings
  // const [triggerDelete] = useBrokersMtmTransportationProvidersDestroyMutation();
  // const handleDelete = useCallback(async () => {
  //   try {
  //     await triggerDelete({ id: setting.id }).unwrap();
  //     dispatch(
  //       show({
  //         title: 'MTM settings deleted successfully',
  //         type: 'success',
  //       })
  //     );
  //   } catch (error) {
  //     dispatch(show({ title: 'Failed to delete MTM settings', type: 'error' }));
  //   }
  // }, [dispatch, setting.id, triggerDelete]);

  const handleEdit = useCallback(() => {
    dispatch(adminActions.setEditingMtmSettingsId(setting.id));
    dispatch(adminActions.setShowMtmSettingsModal(true));
  }, [dispatch, setting.id]);

  const handleInitialize = useCallback(async () => {
    try {
      const { email_text } = await triggerInitializeMtmTransportationProvider({
        mtmTransportationProvider: setting,
      }).unwrap();
      copy(email_text);
    } catch (error) {
      dispatch(
        show({
          title: 'Failed to initialize MTM settings',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  }, [copy, dispatch, setting, triggerInitializeMtmTransportationProvider]);

  const handleGetEmail = useCallback(async () => {
    try {
      const { email_text } = await triggerGetEmail({
        id: setting.id,
      }).unwrap();
      copy(email_text);
    } catch (error) {
      dispatch(
        show({
          title: 'Failed to get email',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  }, [copy, dispatch, setting, triggerGetEmail]);

  const handleSetSecret = useCallback(async () => {
    const userSecret = window.prompt('Enter secret value:');
    if (!userSecret) return;

    try {
      const { detail } = await triggerSetSecret({
        mtmSecret: {
          client_secret: userSecret,
          mtm_transportation_provider_id: setting.id,
        },
      }).unwrap();
      dispatch(
        show({
          title: 'Secret set successfully',
          description: detail || 'The secret has been saved.',
          type: 'success',
        })
      );
    } catch (error) {
      let displayError = formatServerError(error);
      if ('data' in (error as any)) {
        displayError = (error as any).data.detail;
      }

      dispatch(
        show({
          title: 'Failed to set secret',
          description: displayError,
          type: 'error',
        })
      );
    }
  }, [dispatch, setting, triggerSetSecret]);

  const handleCheckStatus = useCallback(async () => {
    try {
      const reasons = await triggerCheckStatus({ id: setting.id }).unwrap();
      dispatch(
        show({
          title: 'MTM Turnback Reasons',
          description:
            reasons.length > 0
              ? reasons.map(r => r.name).join(', ')
              : 'No turnback reasons returned.',
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        show({
          title: 'Failed to retrieve MTM turnback reasons',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  }, [dispatch, setting, triggerCheckStatus]);

  const handleBackfillTrips = useCallback(async () => {
    try {
      const { created_count, found_count } = await triggerBackfillTrips({
        id: setting.id,
      }).unwrap();
    dispatch(show({
      title: 'Backfill complete',
      description: `Created ${created_count} trips, found ${found_count} total`,
      type: 'success'
    }));
    } catch (error) {
      dispatch(
        show({
          title: 'Failed to backfill',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  }, [dispatch, setting, triggerBackfillTrips]);

  return (
    <div className="flex flex-row gap-2">
      <Button
        variant="icon"
        onClick={handleInitialize}
        ariaLabel={`Initialize MTM settings`}
        dataTestId={`initialize-mtm-settings-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <SparklesIcon className="w-[24px]" />
          Initialize
        </div>
      </Button>
      <Button
        variant="icon"
        onClick={handleGetEmail}
        ariaLabel={`Get email`}
        dataTestId={`get-email-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <EnvelopeIcon className="w-[24px]" />
          Get Email
        </div>
      </Button>
      <Button
        variant="icon"
        onClick={handleEdit}
        ariaLabel={`Edit MTM settings`}
        dataTestId={`edit-mtm-settings-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <PencilIcon className="w-[24px]" />
          Edit
        </div>
      </Button>
      <Button
        variant="icon"
        onClick={handleSetSecret}
        ariaLabel={`Set secret for MTM setting`}
        dataTestId={`set-secret-mtm-settings-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <EyeSlashIcon className="w-[24px]" />
          Set Secret
        </div>
      </Button>
      <Button
        variant="icon"
        onClick={handleCheckStatus}
        ariaLabel={`Check the Bambi to MTM connection`}
        dataTestId={`check-bambi-to-mtm-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <CloudIcon className="w-[24px]" />
          Bambi → MTM?
        </div>
      </Button>
      <SimpleTooltip
        trigger={
          <div>
            <Button
              variant="icon"
              onClick={handleBackfillTrips}
              ariaLabel={`Backfill 4 weeks of trips`}
              dataTestId={`check-bambi-to-mtm-${setting.id}`}
            >
              <div
                className={`flex flex-col items-center text-xs ${
                  !setting.last_backfill ? "text-blue-500" : "text-gray-700"
                }`}
                style={{ fontSize: "0.625rem" }}
              >
                <BackfillTripsIcon className="w-[24px]" />
                Backfill Trips
              </div>
            </Button>
          </div>
        }
        content={
          setting.last_backfill
            ? `Last run: ${new Date(setting.last_backfill).toLocaleString()}`
            : "Never run"
        }
      />

      {/* Deleting is not supported <Button
        variant="icon"
        onClick={handleDelete}
        ariaLabel={`Delete MTM settings`}
        dataTestId={`delete-mtm-settings-${setting.id}`}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <TrashIcon className="w-[24px]" />
          Delete
        </div>
      </Button> */}
    </div>
  );
}
